import { useState } from 'react';
import { Loader2, Plus, Trash2, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    useSpeciesRelated,
    useAddSpeciesRelated,
    useRemoveSpeciesRelated,
    useSpeciesList,
    type SpeciesRelationType,
} from '../hooks/useSpeciesAdmin';

const RELATION_LABELS: Record<SpeciesRelationType, string> = {
    similar: 'Similar',
    same_genus: 'Mismo género',
    confusion: 'Confusión habitual',
    predator_prey: 'Depredador/presa',
};

interface SpeciesRelatedPanelProps {
    speciesCatalogId: number;
}

export function SpeciesRelatedPanel({ speciesCatalogId }: SpeciesRelatedPanelProps) {
    const { data: related, isLoading } = useSpeciesRelated(speciesCatalogId);
    const addMutation = useAddSpeciesRelated();
    const removeMutation = useRemoveSpeciesRelated();
    const [search, setSearch] = useState('');
    const [relationType, setRelationType] = useState<SpeciesRelationType>('similar');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { data: searchResults } = useSpeciesList({
        search: search.trim() || undefined,
        limit: 8,
    });

    const candidates = (searchResults ?? []).filter(
        s => s.id !== speciesCatalogId && !related?.some(r => r.related_species_catalog_id === s.id)
    );

    const handleAdd = async () => {
        if (selectedId == null) return;
        setError(null);
        try {
            await addMutation.mutateAsync({
                p_species_catalog_id: speciesCatalogId,
                p_related_species_catalog_id: selectedId,
                p_relation_type: relationType,
            });
            setSearch('');
            setSelectedId(null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Error al añadir relación');
        }
    };

    const handleRemove = async (relatedId: number) => {
        setError(null);
        try {
            await removeMutation.mutateAsync({
                p_species_catalog_id: speciesCatalogId,
                p_related_species_catalog_id: relatedId,
            });
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Error al eliminar relación');
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Link2 size={14} />
                    Especies relacionadas
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                    Similares, confusión de nombres u otras relaciones editoriales.
                </p>
            </div>

            {error && (
                <div className="p-2 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
            )}

            {isLoading ? (
                <Loader2 size={18} className="animate-spin text-muted-foreground" />
            ) : related && related.length > 0 ? (
                <ul className="space-y-2">
                    {related.map(r => (
                        <li
                            key={r.related_species_catalog_id}
                            className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border bg-card/50"
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                    {r.common_names_es || r.scientific_name}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{r.scientific_name}</p>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted mt-1 inline-block">
                                    {RELATION_LABELS[r.relation_type as SpeciesRelationType] ?? r.relation_type}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemove(r.related_species_catalog_id)}
                                disabled={removeMutation.isPending}
                                className="p-1.5 rounded text-muted-foreground hover:text-destructive shrink-0"
                                title="Quitar relación"
                            >
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-muted-foreground">Sin relaciones definidas.</p>
            )}

            <div className="rounded-lg border border-dashed border-border p-3 space-y-3">
                <p className="text-xs font-medium text-muted-foreground">Añadir relación</p>
                <input
                    type="text"
                    value={search}
                    onChange={e => {
                        setSearch(e.target.value);
                        setSelectedId(null);
                    }}
                    placeholder="Buscar especie por nombre..."
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                />
                {search.trim() && candidates.length > 0 && (
                    <ul className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                        {candidates.map(c => (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelectedId(c.id)}
                                    className={cn(
                                        'w-full text-left px-3 py-2 text-sm hover:bg-muted/50',
                                        selectedId === c.id && 'bg-primary/10'
                                    )}
                                >
                                    <span className="font-medium">{c.common_names_es || c.scientific_name}</span>
                                    <span className="text-muted-foreground text-xs block">{c.scientific_name}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <select
                    value={relationType}
                    onChange={e => setRelationType(e.target.value as SpeciesRelationType)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                >
                    {(Object.keys(RELATION_LABELS) as SpeciesRelationType[]).map(k => (
                        <option key={k} value={k}>{RELATION_LABELS[k]}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={handleAdd}
                    disabled={selectedId == null || addMutation.isPending}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
                >
                    {addMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Añadir relación
                </button>
            </div>
        </div>
    );
}
